import { FormControlLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from "@mui/material";
import * as React from "react";
import { IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { TaskTypeClassPreWrap, TaskTypeClassTextAreaSize, TaskTypeClassTextSize, TaskTypeClassVertical } from "ui/Scripts/source/models/enums";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { DisabledInputClickEnabler } from "./DisabledInputClickEnabler";
import { FormFieldMargin } from "./TaskDynamicForm.styles";
import { Base } from "ui/Scripts/source/framework/base";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { getCheckBoxSizeSx } from "./TaskDynamicFormBoolean";
import { getFormFieldTitle, getFormFieldTitleColor } from "./TaskDynamicFormFieldUtils";

interface ITaskDynamicFormStringProp {
    isReadOnly: boolean;
    colIsReadOnly: boolean;
    showFieldName: boolean;
    showFieldNameColor: string;
    taskTypeCol: ITaskTypeCol;
    taskDataRowCol: ITaskDataRowCol;
    onChange: (dataCol: ITaskDataRowCol) => void;
}

export const getRadioButtonSx = (className: string): any => {
    const result = getCheckBoxSizeSx(className) ?? {};
    let classnames = className ?? "";
    if (!classnames) return { ...result, paddingTop: "8px", paddingBottom: "8px" };
    classnames = " " + classnames + " ";
    if (classnames.indexOf(" " + TaskTypeClassTextSize.Small + " ") > -1) return { ...result, paddingTop: "8px", paddingBottom: "9px" };
    if (classnames.indexOf(" " + TaskTypeClassTextSize.Medium + " ") > -1) return { ...result, paddingTop: "8px", paddingBottom: "8px" };
    if (classnames.indexOf(" " + TaskTypeClassTextSize.Large + " ") > -1) return { ...result, paddingTop: "6px", paddingBottom: "6px" };
    if (classnames.indexOf(" " + TaskTypeClassTextSize.Extra + " ") > -1) return { ...result, paddingTop: "5px", paddingBottom: "5px" };
    return { ...result, paddingTop: "8px", paddingBottom: "8px" };
};

export const TaskDynamicFormString = (props: ITaskDynamicFormStringProp) => {
    const handleChange = (event) => {
        const target = event.target;
        const valueStr: string = target.value;
        const newValue = new TaskDataRowCol(props.taskDataRowCol);
        newValue.valueStr = valueStr;
        props.onChange(newValue);
    };

    const handleBlur = (event) => {
        const typeCol = props.taskTypeCol;
        const target = event.target;
        const strValue: string = target.value;
        const newValue = new TaskDataRowCol(props.taskDataRowCol);
        newValue.valueStr = TaskDataRowCol.getTaskDataRowColUiValue(typeCol, strValue, newValue.valueNbr);
        props.onChange(newValue);
    };

    const getTextAreaRowCount = (): number => {
        let classnames = props.taskTypeCol.class ?? "";
        if (!classnames) return 2;
        classnames = " " + classnames + " ";
        if (classnames.indexOf(" " + TaskTypeClassTextAreaSize.Small + " ") > -1) return 2;
        if (classnames.indexOf(" " + TaskTypeClassTextAreaSize.Medium + " ") > -1) return 5;
        if (classnames.indexOf(" " + TaskTypeClassTextAreaSize.Large + " ") > -1) return 10;
        if (classnames.indexOf(" " + TaskTypeClassTextAreaSize.Extra + " ") > -1) return 15;
        return 3;
    };
    
    const getRadioInline = (): boolean => {
        let classnames = props.taskTypeCol.class ?? "";
        if (!classnames) return true;
        classnames = " " + classnames + " ";
        if (classnames.indexOf(" " + TaskTypeClassVertical.Yes + " ") > -1) return false;
        return true;
    };

    const getSelectListStyles = (): string => {
        const classnames = props.taskTypeCol.class ?? "";
        if (!classnames) return null;
        const classes = classnames.split(" ").filter(i => i && i.indexOf("col-") < 0);
        return classes.join(" ");
    };

    const typeCol = props.taskTypeCol;
    const dataCol = props.taskDataRowCol;
    const options = !Base.isNullOrUndefined(typeCol.options) && typeCol.options.length > 0
        ? typeCol.options.map(i => IdTitle.createIdTitle(i, i))
        : (!Base.isNullOrUndefined(typeCol.idOptions) && typeCol.idOptions.length > 0
            ? typeCol.idOptions
            : null);
    const createSelectTextInput = !Base.isNullOrUndefined(options);
    const isRadio = (typeCol.class ?? "").includes("radio");

    const createTextArea = () => {
        return <DisabledInputClickEnabler
            input={<TextField
                name={typeCol.field}
                title={typeCol.title}
                required={typeCol.required}
                disabled={props.colIsReadOnly}
                value={dataCol?.valueStr || ""}
                inputProps={{ maxLength: typeCol.maxLength > 0 ? typeCol.maxLength : null }}
                placeholder={typeCol.placeholder ? typeCol.placeholder : null}
                multiline
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
                fullWidth
                rows={getTextAreaRowCount()}
            />}
            disabled={props.colIsReadOnly}
        />;
    };

    const createTextInput = () => {
        return <DisabledInputClickEnabler
            input={<TextField
                name={typeCol.field}
                title={typeCol.title}
                required={typeCol.required}
                disabled={props.colIsReadOnly}
                value={dataCol?.valueStr || ""}
                inputProps={{ maxLength: typeCol.maxLength > 0 ? typeCol.maxLength : null }}
                placeholder={typeCol.placeholder ? typeCol.placeholder : null}
                autoComplete="off"
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
                fullWidth
            />}
            disabled={props.colIsReadOnly}
        />;
    };

    const createDropdown = () => {
        return <DisabledInputClickEnabler
            input={<Select
                name={typeCol.field}
                title={typeCol.title}
                required={typeCol.required}
                disabled={props.colIsReadOnly}
                value={dataCol?.valueStr || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                size="small"
                fullWidth
                MenuProps={{ classes: { root: "jsonFormData", list: getSelectListStyles() } }}
            >
                {!Base.isNullOrEmpty(typeCol.placeholder) &&
                    <MenuItem key="ph" value="">{typeCol.placeholder}</MenuItem>
                }
                {options.map((option) =>
                    <MenuItem key={option.id} value={option.id}>{option.title}</MenuItem>
                )}
            </Select>}
            disabled={props.colIsReadOnly}
        />;
    };

    const createRadioButtons = () => {
        return <RadioGroup
            row={getRadioInline()}
            onChange={handleChange}
            value={dataCol?.valueStr || ""}
        >
            {options.map((option, index) =>
                <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio
                        sx={getRadioButtonSx(typeCol.class)}
                    />}
                    label={option.title}
                    disabled={props.colIsReadOnly || props.isReadOnly}
                    className="radio"
                    sx={{ marginRight: index < options.length - 1 ? "25px" : 0 }}
                />
            )}
        </RadioGroup>;
    };

    return (
        <FormField title={getFormFieldTitle(typeCol, props.showFieldName)} titleColor={getFormFieldTitleColor(props.showFieldName, props.showFieldNameColor)} required={typeCol.required && !props.isReadOnly} margin={FormFieldMargin}>
            {(props.isReadOnly && !isRadio
                ? <Typography className={typeCol.maxLength > 499 ? TaskTypeClassPreWrap.PreWrap : ""}>{dataCol?.valueStr}</Typography>
                : (!createSelectTextInput
                    ? (typeCol.maxLength > 499
                        ? createTextArea()
                        : createTextInput()
                    )
                    : (!isRadio
                        ? createDropdown()
                        : createRadioButtons()
                    )
                )
            )}
        </FormField>
    );
};